/**
 * @prettier
 */

import createBehavior from '@js/functions/createBehavior.js'
import lockScroll from '@js/utils/lock-scroll.js';
import * as focusTrap from 'focus-trap';
import ImageTemplate from '@components/image/image-src.twig'

function fragmentFromString(strHTML) {
  return document.createRange().createContextualFragment(strHTML)
}

const heroArtworkBehavior = createBehavior(
  'hero-artwork',
  {
    bindUI() {
      this.ui = {}
      this.ui.body = document.body
      this.ui.imageContainer = this.getChild('image-container')
      this.ui.imageContainerInner = this.getChild('image-container-inner')
      this.ui.zoomToggle = this.getChild('zoom-toggle')
      this.ui.zoomModal = this.getChild('zoom-modal')
      this.ui.zoomClose = this.getChild('zoom-close')
      this.ui.zoomViewport = this.getChild('zoom-viewport')
      this.ui.zoomImageContainer = this.getChild('zoom-image-container')
      this.ui.zoomImage = this.ui.zoomImageContainer.querySelector('img')
      this.ui.thumbs = this.getChildren('thumb')

      this.ui.thumbsArr = Array.from(this.ui.thumbs)
      this.ui.captions = document.querySelectorAll('.caption span[data-caption-index]')
    },
    bindEvents() {
      this.ui.zoomImage.addEventListener('load', this.handleZoomImageLoaded)
      this.ui.zoomToggle.addEventListener('click', this.handleZoomToggleClick)
      this.ui.zoomClose.addEventListener('click', this.handleZoomCloseClick)
      this.ui.zoomModal.addEventListener('click', this.handleZoomModalClick)
      document.addEventListener('keydown', this.handleDocumentKeydown)

      for (let image of this.ui.thumbsArr) {
        image.addEventListener('click', (evt) => {
          evt.preventDefault()
          this.handleThumbClick(image)
        })
        image.addEventListener('keydown', this.handleThumbKeydown)
      }
    },
    unbindEvents() {
      document.removeEventListener('keydown', this.handleDocumentKeydown)
    },
    handleZoomImageLoaded() {
      this.centerZoomViewport()
    },
    getZoomLabel() {
      return (
        this.currentZoomLabel ||
        this.ui.imageContainer.querySelector('img')?.getAttribute('alt') ||
        ''
      )
    },
    updateZoomModalLabel() {
      const imageLabel = this.getZoomLabel()
      const modalLabel = imageLabel
        ? `${this.ui.zoomToggle.dataset.labelZoomIn} ${imageLabel}`
        : this.ui.zoomToggle.dataset.labelZoomIn

      this.ui.zoomModal.setAttribute('aria-label', modalLabel)
    },
    updateZoomToggleState() {
      const isZoomOpen = this.node.classList.contains('is-zoom-open')
      const actionLabel = isZoomOpen
        ? this.ui.zoomToggle.dataset.labelClose
        : this.ui.zoomToggle.dataset.labelZoomIn
      const imageLabel = this.getZoomLabel()
      const label = imageLabel ? `${actionLabel} ${imageLabel}` : actionLabel

      this.ui.zoomToggle.setAttribute('aria-expanded', String(isZoomOpen))
      this.ui.zoomToggle.setAttribute('aria-label', label)
      this.updateZoomModalLabel()
    },
    centerZoomViewport() {
      const { zoomViewport, zoomImage } = this.ui

      if (!zoomViewport || !zoomImage) {
        return
      }

      const maxScrollLeft = Math.max(
        zoomViewport.scrollWidth - zoomViewport.clientWidth,
        0,
      )
      const maxScrollTop = Math.max(
        zoomViewport.scrollHeight - zoomViewport.clientHeight,
        0,
      )

      zoomViewport.scrollLeft = maxScrollLeft / 2
      //zoomViewport.scrollTop = maxScrollTop / 2
      zoomViewport.scrollTop = 0
    },
    openZoom() {
      this.lastFocusedElement = document.activeElement
      this.ui.zoomModal.hidden = false
      this.node.classList.add('is-zoom-open')
      this.ui.body.classList.add('has-modal')
      this.updateZoomToggleState()

      lockScroll.enable();
      if (!this.focusTrap) {
        this.focusTrap = focusTrap.createFocusTrap(this.ui.zoomModal);
      }
      this.focusTrap.activate();

      requestAnimationFrame(() => {
        this.centerZoomViewport();
        this.ui.zoomClose.focus();
      })
    },
    handleZoomToggleClick() {
      if (this.node.classList.contains('is-zoom-open')) {
        this.closeZoom({ returnFocus: false })
        return
      }

      this.openZoom()
    },
    handleZoomCloseClick() {
      this.closeZoom()
    },
    handleZoomModalClick(event) {
      if (event.target === this.ui.zoomModal) {
        this.closeZoom()
      }
    },
    handleDocumentKeydown(event) {
      if (
        event.key === 'Escape' &&
        this.node.classList.contains('is-zoom-open')
      ) {
        this.closeZoom()
      }
    },
    closeZoom({ returnFocus = true } = {}) {
      this.ui.zoomModal.hidden = true
      this.node.classList.remove('is-zoom-open')
      this.ui.body.classList.remove('has-modal')
      this.updateZoomToggleState()

      if (returnFocus && this.lastFocusedElement instanceof HTMLElement) {
        lockScroll.disable();
        this.focusTrap.deactivate();
        this.lastFocusedElement.focus()
      }
    },
    preloadImage(el) {
      return new Promise((resolve, reject) => {
        if (el.complete) {
          resolve()
        } else {
          let handleLoad = () => {
            el.removeEventListener('load', handleLoad)
            resolve()
          }
          el.addEventListener('load', handleLoad)
        }
      })
    },
    setActiveThumb(image, { focus = false } = {}) {
      const clickedThumbIndex = this.ui.thumbsArr.indexOf(image)
      this.ui.thumbsArr.forEach((thumb, index) => {
        const isCurrent = thumb === image
        thumb.classList.toggle('is-current', isCurrent)
        thumb.setAttribute('aria-selected', String(isCurrent))
        thumb.setAttribute('tabindex', isCurrent ? '0' : '-1')

        if (this.ui.captions[index]) {
          this.ui.captions[index].classList.toggle('is-active', isCurrent)
        }
      })

      this.currentThumbIndex = clickedThumbIndex
      this.ui.imageContainer.setAttribute('aria-labelledby', image.id)

      if (focus) {
        image.focus()
      }
    },
    handleThumbKeydown(event) {
      const { key, currentTarget } = event
      const currentIndex = this.ui.thumbsArr.indexOf(currentTarget)
      let nextIndex = currentIndex

      if (key === 'ArrowRight' || key === 'ArrowDown') {
        nextIndex = (currentIndex + 1) % this.ui.thumbsArr.length
      } else if (key === 'ArrowLeft' || key === 'ArrowUp') {
        nextIndex =
          (currentIndex - 1 + this.ui.thumbsArr.length) %
          this.ui.thumbsArr.length
      } else if (key === 'Home') {
        nextIndex = 0
      } else if (key === 'End') {
        nextIndex = this.ui.thumbsArr.length - 1
      } else {
        return
      }

      event.preventDefault()
      this.handleThumbClick(this.ui.thumbsArr[nextIndex], { focus: true })
    },
    handleThumbClick(image, options = {}) {
      const heroArtworkThumbData = image.dataset.heroArtworkThumb

      if (!heroArtworkThumbData) {
        console.error('No image data provided in the HTML')
        return
      }
      let json
      try {
        json = JSON.parse(heroArtworkThumbData)
      } catch (error) {
        console.error('Invalid JSON format in data-heroArtworkThumb:', error)
        return
      }

      if (!json.standard || !json.zoom) {
        console.error('Both standard and zoom images must be provided in the HTML')
        return
      }

      this.setActiveThumb(image, options)
      this.currentZoomLabel = json.standard.alt || ''
      this.closeZoom({ returnFocus: false })

      const imageHTML = ImageTemplate({
        ...json.standard,
        lazy: false,
      })

      const imageFragment = fragmentFromString(imageHTML)
      const newImage = imageFragment.querySelector('.image')
      const newImageImg = newImage.querySelector('img')

      // Clear existing content in the image container
      Array.from(this.ui.imageContainerInner.children).forEach((el) => el.remove())
      this.ui.imageContainerInner.appendChild(newImage)


      const zoomImageHTML = ImageTemplate({
        ...json.zoom,
        lazy: false,
      })
      const zoomImageFragment = fragmentFromString(zoomImageHTML)
      const newZoomImage = zoomImageFragment.querySelector('.image')
      const newZoomImageImg = newZoomImage.querySelector('img')


      Array.from(this.ui.zoomImageContainer.children).forEach((el) => el.remove())
      this.ui.zoomImageContainer.appendChild(newZoomImage)


      newImageImg.classList.add('is-loading')
      this.ui.imageContainerInner.classList.add('is-fading')

      const timeoutPromise = new Promise((resolve) => setTimeout(resolve, 300))

      Promise.all([this.preloadImage(newImageImg), timeoutPromise]).then(() => {
        newImageImg.classList.remove('is-loading')
        this.ui.imageContainerInner.classList.remove('is-fading')
        this.currentZoomLabel = newImageImg.getAttribute('alt') || ''
        this.updateZoomToggleState()
      })

      this.preloadImage(newZoomImageImg).then(() => {
        this.bindUI()
        this.updateZoomToggleState()
        this.centerZoomViewport()
      })
    },
  },
  {
    init() {
      this.lastFocusedElement = null
      this.bindUI()
      this.currentZoomLabel =
        this.ui.imageContainer.querySelector('img')?.getAttribute('alt') || ''
      this.updateZoomToggleState()
      this.bindEvents()
    },
    destroy() {
      this.unbindEvents()
    },
  },
)

export default heroArtworkBehavior
